import React, { useState, useRef } from 'react';
import { Download, Search, Loader, Users, BarChart3 } from 'lucide-react';
import { formatDate as formatDateThai, firstDayOfMonthStr, todayStr } from '../utils/dateUtils';
import MaskedDatePicker from './MaskedDatePicker';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import salesTeamService from '../services/salesTeamService';

const API_BASE_URL = 'https://apisaleeasy.psisat.com';
const COMPANIES = ['045', '043', '012'];

const ProductCategorySalesTeamComponent = () => {
  const [startDate, setStartDate] = useState(firstDayOfMonthStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0, step: '' });
  const [sortConfig, setSortConfig] = useState({ key: 'total', direction: 'desc' });
  const cancelRef = useRef(false);

  // Fetch invoices for a salesman from a specific company
  const fetchSalesmanInvoices = async (salesmanCode, sd, ed, companyCode) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sales-analytics/salesman`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          salesman_code: salesmanCode,
          date_range: { start_date: sd, end_date: ed, group_by: 'month' },
          filters: { company_code: companyCode }
        })
      });
      if (!response.ok) return [];
      const data = await response.json();
      return data.status ? (data.data?.invoices || []) : [];
    } catch {
      return [];
    }
  };

  // Fetch category summary for an invoice from a specific company
  const fetchInvoiceCategorySummary = async (invoiceNumber, companyCode) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/inv/product-category-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Inv_Number: invoiceNumber.trim(), CompanyCode: companyCode })
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data.status ? (data.data || null) : null;
    } catch {
      return null;
    }
  };

  // Fetch CN category summary
  const fetchCnCategorySummary = async (cnNumber, companyCode) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cn/category-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cn_number: cnNumber, company_code: companyCode })
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data.status && data.data ? data.data : null;
    } catch {
      return null;
    }
  };

  // Get category analytics for a salesman from one company (invoices + CN deductions)
  const getCategoryAnalyticsForCompany = async (salesmanCode, sd, ed, companyCode, allCNs) => {
    // Seminar = บัตรสัมมนา แสดงแยก ไม่นับรวมใน 5 หมวด
    const categorySummary = { SAT: 0, Solar: 0, MTF: 0, MTB: 0, Other: 0, Seminar: 0 };

    const invoices = await fetchSalesmanInvoices(salesmanCode, sd, ed, companyCode);
    if (invoices.length === 0) return categorySummary;

    // Accumulate category amounts from each invoice
    for (let i = 0; i < invoices.length; i += 5) {
      const batch = invoices.slice(i, i + 5);
      const results = await Promise.allSettled(
        batch.map(inv => fetchInvoiceCategorySummary(inv.invoice_number, companyCode))
      );
      results.forEach(result => {
        if (result.status === 'fulfilled' && result.value) {
          Object.entries(result.value.summary_by_category || {}).forEach(([category, amount]) => {
            const beforeVAT = Math.round((Number(amount) || 0) / 1.07 * 100) / 100;
            const target = categorySummary.hasOwnProperty(category) && category !== 'Seminar' ? category : 'Other';
            categorySummary[target] += beforeVAT;
          });
          categorySummary.Seminar += Math.round((Number(result.value.excluded_amount) || 0) / 1.07 * 100) / 100;
        }
      });
    }

    // Deduct credit notes for this company
    const salesmanCNs = (allCNs || []).filter(cn => (cn.SalesmanCode || '').trim() === salesmanCode);
    for (let i = 0; i < salesmanCNs.length; i += 5) {
      const batch = salesmanCNs.slice(i, i + 5);
      const results = await Promise.allSettled(
        batch.map(cn => fetchCnCategorySummary((cn.CN_Number || '').trim(), companyCode))
      );
      results.forEach(result => {
        if (result.status === 'fulfilled' && result.value) {
          const cnData = result.value;
          const cnAmount = cnData.cn_amount || 0;
          const invoiceCatAmounts = { ...(cnData.invoice_category_amounts || {}) };
          // บัตรสัมมนาในบิลต้นทาง → หักจากช่อง Seminar ตามสัดส่วน
          if (Number(cnData.invoice_excluded_amount) > 0) invoiceCatAmounts.Seminar = Number(cnData.invoice_excluded_amount);
          const catTotal = Object.values(invoiceCatAmounts).reduce((s, a) => s + (Number(a) || 0), 0);
          if (catTotal > 0) {
            Object.entries(invoiceCatAmounts).forEach(([cat, amt]) => {
              const proportion = (Number(amt) || 0) / catTotal;
              const deduction = Math.round((cnAmount * proportion) / 1.07 * 100) / 100;
              const target = categorySummary.hasOwnProperty(cat) ? cat : 'Other';
              categorySummary[target] -= deduction;
            });
          }
        }
      });
    }

    // Floor at 0 (prevent CN over-deduction from making categories negative)
    ['SAT', 'Solar', 'MTF', 'MTB', 'Other', 'Seminar'].forEach(k => {
      categorySummary[k] = Math.round(Math.max(0, categorySummary[k] || 0) * 100) / 100;
    });

    return categorySummary;
  };

  // Merge category summaries from multiple companies
  const mergeCategoryData = (summaries) => {
    const merged = { SAT: 0, Solar: 0, MTF: 0, MTB: 0, Other: 0, Seminar: 0 };
    summaries.forEach(s => {
      ['SAT', 'Solar', 'MTF', 'MTB', 'Other', 'Seminar'].forEach(k => {
        merged[k] = Math.round((merged[k] + (s[k] || 0)) * 100) / 100;
      });
    });
    return merged;
  };

  const fetchData = async () => {
    if (!startDate || !endDate) {
      setError('กรุณาเลือกวันที่เริ่มต้นและสิ้นสุด');
      return;
    }

    setLoading(true);
    setError(null);
    setTableData([]);
    setProgress({ current: 0, total: 0, step: 'กำลังดึงข้อมูลทีมขาย...' });
    cancelRef.current = false;

    try {
      // Step 1: Get all teams with members
      const teamsResponse = await salesTeamService.getTeamsWithMembers();
      if (!teamsResponse.success) {
        throw new Error('ไม่สามารถดึงข้อมูลทีมขายได้');
      }

      const teams = teamsResponse.data || [];
      const allMembers = [];
      const seenCodes = new Set();
      teams.forEach(team => {
        const teamName = team.team_name || 'ไม่ระบุทีม';
        (team.members || []).forEach(member => {
          const code = member.member_saleman_code || '';
          if (!code || seenCodes.has(code)) return;
          seenCodes.add(code);
          allMembers.push({
            salesman_code: code,
            salesman_name: `${member.sale_name || ''} ${member.sale_lastname || ''}`.trim() || 'ไม่ระบุ',
            team_name: teamName
          });
        });
      });

      if (allMembers.length === 0) {
        setError('ไม่พบข้อมูลสมาชิกทีมขาย');
        setLoading(false);
        return;
      }

      // Step 2: Get sales summaries from all 3 companies (authoritative totals)
      setProgress({ current: 0, total: 0, step: 'กำลังดึงยอดขายทุกบริษัท...' });

      const [salesRes, maqRes, ramRes] = await Promise.all([
        salesTeamService.getTeamSalesSummary(startDate, endDate),
        salesTeamService.getMaqTeamSalesSummary(startDate, endDate),
        salesTeamService.getRamTeamSalesSummary(startDate, endDate)
      ]);

      // Build map: salesman_code -> amount_before_vat (combined all companies)
      const salesMap = {};
      const processSalesData = (response) => {
        if (!response.success || !response.data) return;
        const teamsData = response.data.teams || response.data || [];
        if (Array.isArray(teamsData)) {
          teamsData.forEach(team => {
            (team.members_performance || []).forEach(member => {
              const code = (member.member_saleman_code || '').trim();
              if (!code) return;
              salesMap[code] = (salesMap[code] || 0) + Number(member.amount_before_vat || 0);
            });
          });
        }
      };
      processSalesData(salesRes);
      processSalesData(maqRes);
      processSalesData(ramRes);

      // Step 3: Pre-fetch credit notes for all 3 companies
      setProgress({ current: 0, total: 0, step: 'กำลังดึงข้อมูลใบลดหนี้...' });
      const [cns045, cns043, cns012] = await Promise.all([
        salesTeamService.searchAllCreditNotes(startDate, endDate, '045'),
        salesTeamService.searchAllCreditNotes(startDate, endDate, '043').catch(() => []),
        salesTeamService.searchAllCreditNotes(startDate, endDate, '012').catch(() => [])
      ]);

      // Step 4: Get category analytics for each member (all 3 companies)
      setProgress({ current: 0, total: allMembers.length, step: 'กำลังวิเคราะห์หมวดสินค้า...' });

      const results = [];
      const batchSize = 3;
      const sd = salesTeamService.formatDateForAPI(startDate);
      const ed = salesTeamService.formatDateForAPI(endDate);

      for (let i = 0; i < allMembers.length; i += batchSize) {
        if (cancelRef.current) break;

        const batch = allMembers.slice(i, i + batchSize);

        const batchResults = await Promise.allSettled(
          batch.map(member => Promise.all([
            getCategoryAnalyticsForCompany(member.salesman_code, sd, ed, '045', cns045),
            getCategoryAnalyticsForCompany(member.salesman_code, sd, ed, '043', cns043),
            getCategoryAnalyticsForCompany(member.salesman_code, sd, ed, '012', cns012)
          ]).then(([r045, r043, r012]) => mergeCategoryData([r045, r043, r012])))
        );

        batchResults.forEach((result, idx) => {
          const member = batch[idx];
          let categoryData = { SAT: 0, Solar: 0, MTF: 0, MTB: 0, Other: 0, Seminar: 0 };

          if (result.status === 'fulfilled') {
            categoryData = result.value;
          }

          // Authoritative total from team-sales-summary (all 3 companies)
          const authTotal = Math.round((salesMap[member.salesman_code] || 0) * 100) / 100;

          // Category subtotal (from all 3 companies invoice analysis)
          const catSubtotal = Math.round(
            (categoryData.SAT + categoryData.Solar + categoryData.MTF + categoryData.MTB + categoryData.Other) * 100
          ) / 100;

          // ยอดรวมไม่นับบัตรสัมมนา (authTotal รวมบัตรสัมมนามาด้วย จึงหักออก)
          const totalExSeminar = Math.round((authTotal - categoryData.Seminar) * 100) / 100;

          // Any remaining difference → Other (rounding or missing invoices)
          const diff = Math.round((totalExSeminar - catSubtotal) * 100) / 100;
          if (diff > 0) {
            categoryData.Other = Math.round((categoryData.Other + diff) * 100) / 100;
          }

          results.push({
            salesman_code: member.salesman_code,
            salesman_name: member.salesman_name,
            team_name: member.team_name,
            ...categoryData,
            total: totalExSeminar
          });
        });

        setProgress({ current: Math.min(i + batchSize, allMembers.length), total: allMembers.length, step: 'กำลังวิเคราะห์หมวดสินค้า...' });
      }

      // Filter out members with zero sales
      const filteredResults = results.filter(r => r.total !== 0 || r.Seminar !== 0);
      filteredResults.sort((a, b) => b.total - a.total);
      setTableData(filteredResults);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'เกิดข้อผิดพลาดในการดึงข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = () => {
    const sorted = [...tableData];
    sorted.sort((a, b) => {
      const valA = a[sortConfig.key] || 0;
      const valB = b[sortConfig.key] || 0;
      if (sortConfig.direction === 'asc') return valA - valB;
      return valB - valA;
    });
    return sorted;
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0.00';
    return Number(num).toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕';
    return sortConfig.direction === 'desc' ? '↓' : '↑';
  };

  const formatExcelNumber = (num) => {
    if (!num && num !== 0) return '0.00';
    return Number(num).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const exportToExcel = () => {
    if (tableData.length === 0) return;

    const sortedData = getSortedData();
    const dateRangeText = `ช่วงวันที่: ${formatDateThai(startDate)} - ${formatDateThai(endDate)}`;

    // Header rows
    const headerRows = [
      ['รายงานหมวดสินค้าทีมขาย'],
      [dateRangeText],
      [] // blank row
    ];

    const exportData = sortedData.map((item, index) => ({
      'ลำดับ': index + 1,
      'รหัสเซลล์': item.salesman_code,
      'ชื่อ': item.salesman_name,
      'ทีม': item.team_name,
      'SAT': formatExcelNumber(item.SAT),
      'Solar': formatExcelNumber(item.Solar),
      'MTF (โรงงาน)': formatExcelNumber(item.MTF),
      'MTB (นำเข้า)': formatExcelNumber(item.MTB),
      'อื่นๆ': formatExcelNumber(item.Other),
      'ยอดรวม': formatExcelNumber(item.total),
      'บัตรสัมมนา (ไม่นับ)': formatExcelNumber(item.Seminar)
    }));

    // Add grand total row
    exportData.push({
      'ลำดับ': '',
      'รหัสเซลล์': '',
      'ชื่อ': '',
      'ทีม': 'รวมทั้งหมด',
      'SAT': formatExcelNumber(grandSAT),
      'Solar': formatExcelNumber(grandSolar),
      'MTF (โรงงาน)': formatExcelNumber(grandMTF),
      'MTB (นำเข้า)': formatExcelNumber(grandMTB),
      'อื่นๆ': formatExcelNumber(grandOther),
      'ยอดรวม': formatExcelNumber(grandTotal),
      'บัตรสัมมนา (ไม่นับ)': formatExcelNumber(grandSeminar)
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(headerRows);

    // Append data rows starting after header
    XLSX.utils.sheet_add_json(ws, exportData, { origin: 'A4' });

    // Set column widths
    ws['!cols'] = [
      { wch: 8 },   // ลำดับ
      { wch: 12 },  // รหัสเซลล์
      { wch: 25 },  // ชื่อ
      { wch: 20 },  // ทีม
      { wch: 18 },  // SAT
      { wch: 18 },  // Solar
      { wch: 20 },  // MTF (โรงงาน)
      { wch: 18 },  // MTB (นำเข้า)
      { wch: 18 },  // อื่นๆ
      { wch: 20 },  // ยอดรวม
      { wch: 20 }   // บัตรสัมมนา (ไม่นับ)
    ];

    // Merge title row across all columns
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 10 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 10 } }
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'หมวดสินค้าทีมขาย');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

    const startStr = startDate;
    const endStr = endDate;
    saveAs(
      new Blob([wbout], { type: 'application/octet-stream' }),
      `หมวดสินค้าทีมขาย_${startStr}_${endStr}.xlsx`
    );
  };

  const grandTotal = tableData.reduce((sum, r) => sum + r.total, 0);
  const grandSAT = tableData.reduce((sum, r) => sum + r.SAT, 0);
  const grandSolar = tableData.reduce((sum, r) => sum + r.Solar, 0);
  const grandMTF = tableData.reduce((sum, r) => sum + r.MTF, 0);
  const grandMTB = tableData.reduce((sum, r) => sum + r.MTB, 0);
  const grandOther = tableData.reduce((sum, r) => sum + r.Other, 0);
  const grandSeminar = tableData.reduce((sum, r) => sum + (r.Seminar || 0), 0);
  const sortedData = getSortedData();

  return (
    <div className="p-4 md:p-6 max-w-full">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">หมวดสินค้าทีมขาย</h2>
            <p className="text-sm text-gray-500">เรียงลำดับยอดขายแยกตามประเภทสินค้า (รวมทุกบริษัท)</p>
          </div>
        </div>
      </div>

      {/* Search Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">วันที่เริ่มต้น</label>
            <MaskedDatePicker
              value={startDate}
              onChange={setStartDate}
              className="w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholderText="เลือกวันที่"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">วันที่สิ้นสุด</label>
            <MaskedDatePicker
              value={endDate}
              onChange={setEndDate}
              className="w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholderText="เลือกวันที่"
            />
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Search className="w-4 h-4" />
            )}
            <span>{loading ? 'กำลังค้นหา...' : 'ค้นหา'}</span>
          </button>
          {tableData.length > 0 && (
            <button
              onClick={exportToExcel}
              className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export Excel</span>
            </button>
          )}
        </div>
      </div>

      {/* Loading Progress */}
      {loading && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Loader className="w-5 h-5 text-blue-600 animate-spin" />
            <span className="text-sm font-medium text-blue-800">
              {progress.step || 'กำลังเตรียมข้อมูล...'}
              {progress.total > 0 && ` (${progress.current}/${progress.total} เซลล์)`}
            </span>
          </div>
          {progress.total > 0 && (
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(progress.current / progress.total) * 100}%` }}
              ></div>
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Results Table */}
      {tableData.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Summary */}
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">
                พบข้อมูล {tableData.length} เซลล์
              </span>
            </div>
            <div className="text-right">
              <span className="text-sm font-bold text-indigo-700">
                ยอดรวมทั้งหมด: {formatNumber(grandTotal)} บาท
              </span>
              {grandSeminar > 0 && (
                <span className="block text-xs text-gray-500">
                  บัตรสัมมนา (ไม่นับในยอดรวม): {formatNumber(grandSeminar)} บาท
                </span>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-indigo-50 to-blue-50">
                  <th className="px-4 py-3 text-center font-semibold text-gray-700 whitespace-nowrap">ลำดับ</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">รหัสเซลล์</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">ชื่อ</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">ทีม</th>
                  <th
                    className="px-4 py-3 text-right font-semibold text-gray-700 whitespace-nowrap cursor-pointer hover:text-indigo-600"
                    onClick={() => handleSort('SAT')}
                  >
                    SAT {getSortIcon('SAT')}
                  </th>
                  <th
                    className="px-4 py-3 text-right font-semibold text-gray-700 whitespace-nowrap cursor-pointer hover:text-indigo-600"
                    onClick={() => handleSort('Solar')}
                  >
                    Solar {getSortIcon('Solar')}
                  </th>
                  <th
                    className="px-4 py-3 text-right font-semibold text-gray-700 whitespace-nowrap cursor-pointer hover:text-indigo-600"
                    onClick={() => handleSort('MTF')}
                    title="Mounting จากโรงงาน (SOL-MTF)"
                  >
                    MTF {getSortIcon('MTF')}
                  </th>
                  <th
                    className="px-4 py-3 text-right font-semibold text-gray-700 whitespace-nowrap cursor-pointer hover:text-indigo-600"
                    onClick={() => handleSort('MTB')}
                    title="Mounting นำเข้า (SOL-MTB)"
                  >
                    MTB {getSortIcon('MTB')}
                  </th>
                  <th
                    className="px-4 py-3 text-right font-semibold text-gray-700 whitespace-nowrap cursor-pointer hover:text-indigo-600"
                    onClick={() => handleSort('Other')}
                  >
                    อื่นๆ {getSortIcon('Other')}
                  </th>
                  <th
                    className="px-4 py-3 text-right font-semibold text-gray-700 whitespace-nowrap cursor-pointer hover:text-indigo-600"
                    onClick={() => handleSort('total')}
                  >
                    ยอดรวม {getSortIcon('total')}
                  </th>
                  <th
                    className="px-4 py-3 text-right font-semibold text-gray-500 whitespace-nowrap cursor-pointer hover:text-indigo-600"
                    onClick={() => handleSort('Seminar')}
                    title="บัตรสัมมนา — แสดงแยก ไม่นับในยอดรวม"
                  >
                    บัตรสัมมนา (ไม่นับ) {getSortIcon('Seminar')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row, index) => (
                  <tr
                    key={row.salesman_code}
                    className={`border-t border-gray-100 hover:bg-indigo-50/30 transition-colors ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                    }`}
                  >
                    <td className="px-4 py-2.5 text-center text-gray-500">{index + 1}</td>
                    <td className="px-4 py-2.5 font-mono text-indigo-700 font-medium">{row.salesman_code}</td>
                    <td className="px-4 py-2.5 text-gray-900">{row.salesman_name}</td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {row.team_name}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-700">{formatNumber(row.SAT)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-700">{formatNumber(row.Solar)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-700">{formatNumber(row.MTF)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-700">{formatNumber(row.MTB)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-700">{formatNumber(row.Other)}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-bold text-indigo-700">{formatNumber(row.total)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-400">{formatNumber(row.Seminar)}</td>
                  </tr>
                ))}
              </tbody>
              {/* Grand Total Footer */}
              <tfoot>
                <tr className="bg-gradient-to-r from-indigo-100 to-blue-100 border-t-2 border-indigo-300">
                  <td colSpan={4} className="px-4 py-3 text-right font-bold text-gray-800">รวมทั้งหมด</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-gray-800">{formatNumber(grandSAT)}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-gray-800">{formatNumber(grandSolar)}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-gray-800">{formatNumber(grandMTF)}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-gray-800">{formatNumber(grandMTB)}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-gray-800">{formatNumber(grandOther)}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-indigo-800 text-base">{formatNumber(grandTotal)}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-gray-500">{formatNumber(grandSeminar)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && tableData.length === 0 && !error && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-500 mb-2">เลือกช่วงวันที่แล้วกดค้นหา</h3>
          <p className="text-sm text-gray-400">ระบบจะแสดงยอดขายแยกตามหมวดสินค้าของแต่ละเซลล์</p>
        </div>
      )}
    </div>
  );
};

export default ProductCategorySalesTeamComponent;
